import { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Checkbox,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  HStack,
  IconButton,
  Input,
  Select,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  VStack,
} from '@chakra-ui/react';
import { FiCamera, FiCopy, FiPlus, FiPrinter, FiTrash2 } from 'react-icons/fi';
const uuidv4 = () => crypto.randomUUID();

import { useToast } from '@/client/hooks/useToast';
import { useBatchMasterData } from '@/lib/sdk/master-data/queries';
import { DEFAULT_COLUMN_CONFIG } from '@/lib/sdk/master-data-categories/types';
import type { MasterDataColumnConfig } from '@/lib/sdk/master-data-categories/types';
import type { MasterDataRow } from '@/lib/sdk/master-data/types';
import type { MasterDataFieldDefinition, FieldOption, ImageFieldConfig } from '@/lib/sdk/master-data-field-definitions/types';
import { useCreateImageUploadSession, usePollImageUploadSession } from '@/lib/sdk/image-upload-sessions/queries';
import { ReferenceSearchInput } from './ReferenceSearchInput';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface BulkRow {
  _localId: string;
  id?: number;
  key?: string;
  label: string;
  parent_key: string;
  lat: string;
  lng: string;
  fields: Record<string, string>;
  _errors?: Record<string, string>;
}

export type EditorMode = 'create' | 'edit';

interface FillState {
  srcRowIdx: number;
  col: string;
  value: string;
  endRow: number;
}

// ─── Reducer ──────────────────────────────────────────────────────────────────

type Action =
  | { type: 'ADD_ROWS'; count: number; fieldDefs: MasterDataFieldDefinition[] }
  | { type: 'SET_CELL'; localId: string; col: string; value: string }
  | { type: 'DELETE_ROW'; localId: string }
  | { type: 'DUPLICATE_ROW'; localId: string }
  | { type: 'SET_ROW_ERRORS'; localId: string; errors: Record<string, string> }
  | { type: 'CLEAR_ERRORS' }
  | { type: 'RESET'; rows: BulkRow[] };

function blankRow(fieldDefs: MasterDataFieldDefinition[]): BulkRow {
  const fields: Record<string, string> = {};
  for (const def of fieldDefs) fields[def.field_name] = '';
  return { _localId: uuidv4(), label: '', parent_key: '', lat: '', lng: '', fields };
}

function reducer(state: BulkRow[], action: Action): BulkRow[] {
  switch (action.type) {
    case 'ADD_ROWS':
      return [...state, ...Array.from({ length: action.count }, () => blankRow(action.fieldDefs))];
    case 'SET_CELL':
      return state.map((r) => {
        if (r._localId !== action.localId) return r;
        if (action.col === 'label') return { ...r, label: action.value, _errors: { ...r._errors, label: '' } };
        if (action.col === 'parent_key') return { ...r, parent_key: action.value, _errors: { ...r._errors, parent_key: '' } };
        if (action.col === 'lat') return { ...r, lat: action.value, _errors: { ...r._errors, lat: '' } };
        if (action.col === 'lng') return { ...r, lng: action.value, _errors: { ...r._errors, lng: '' } };
        return { ...r, fields: { ...r.fields, [action.col]: action.value }, _errors: { ...r._errors, [action.col]: '' } };
      });
    case 'DELETE_ROW':
      return state.filter((r) => r._localId !== action.localId);
    case 'DUPLICATE_ROW': {
      const idx = state.findIndex((r) => r._localId === action.localId);
      if (idx === -1) return state;
      const src = state[idx];
      const copy: BulkRow = { _localId: uuidv4(), label: src.label, parent_key: src.parent_key, lat: src.lat, lng: src.lng, fields: { ...src.fields } };
      return [...state.slice(0, idx + 1), copy, ...state.slice(idx + 1)];
    }
    case 'SET_ROW_ERRORS':
      return state.map((r) => r._localId === action.localId ? { ...r, _errors: action.errors } : r);
    case 'CLEAR_ERRORS':
      return state.map((r) => ({ ...r, _errors: {} }));
    case 'RESET':
      return action.rows;
    default:
      return state;
  }
}

// ─── Fill Handle ──────────────────────────────────────────────────────────────

function FillHandle({ onMouseDown }: { onMouseDown: (e: React.MouseEvent) => void }) {
  return (
    <Box
      className="fill-handle"
      position="absolute"
      bottom="-4px"
      right="-4px"
      w="8px"
      h="8px"
      bg="brand.500"
      border="1.5px solid white"
      borderRadius="1px"
      cursor="crosshair"
      zIndex={2}
      onMouseDown={onMouseDown}
    />
  );
}

// ─── Cell components ───────────────────────────────────────────────────────────

interface CellProps {
  id?: string;
  value: string;
  onChange: (v: string) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLElement>) => void;
  error?: string;
  onFillStart?: () => void;
}

function TextCell({ id, value, onChange, onKeyDown, error, onFillStart }: CellProps) {
  return (
    <Box
      position="relative"
      sx={{ '& .fill-handle': { opacity: 0, transition: 'opacity 0.1s' }, '&:hover .fill-handle': { opacity: 1 } }}
    >
      <Tooltip label={error} isDisabled={!error} placement="top" hasArrow bg="red.500">
        <Input
          id={id}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={onKeyDown}
          size="sm"
          variant="unstyled"
          bg="transparent"
          px={2}
          py={1}
          h="32px"
          borderRadius="none"
          borderWidth="1px"
          borderColor={error ? 'red.400' : 'border-default'}
          _hover={{ borderColor: error ? 'red.500' : 'border-hover' }}
          _focus={{ borderColor: 'brand.500', outline: 'none' }}
        />
      </Tooltip>
      {onFillStart && (
        <FillHandle onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); onFillStart(); }} />
      )}
    </Box>
  );
}

function NumberCell({ id, value, onChange, onKeyDown, error, onFillStart }: CellProps) {
  return (
    <Box
      position="relative"
      sx={{ '& .fill-handle': { opacity: 0, transition: 'opacity 0.1s' }, '&:hover .fill-handle': { opacity: 1 } }}
    >
      <Tooltip label={error} isDisabled={!error} placement="top" hasArrow bg="red.500">
        <Input
          id={id}
          type="number"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={onKeyDown}
          size="sm"
          variant="unstyled"
          bg="transparent"
          px={2}
          py={1}
          h="32px"
          borderRadius="none"
          borderWidth="1px"
          borderColor={error ? 'red.400' : 'border-default'}
          _hover={{ borderColor: error ? 'red.500' : 'border-hover' }}
          _focus={{ borderColor: 'brand.500', outline: 'none' }}
        />
      </Tooltip>
      {onFillStart && (
        <FillHandle onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); onFillStart(); }} />
      )}
    </Box>
  );
}

function DateCell({ id, value, onChange, onKeyDown, error, onFillStart }: CellProps) {
  return (
    <Box
      position="relative"
      sx={{ '& .fill-handle': { opacity: 0, transition: 'opacity 0.1s' }, '&:hover .fill-handle': { opacity: 1 } }}
    >
      <Tooltip label={error} isDisabled={!error} placement="top" hasArrow bg="red.500">
        <Input
          id={id}
          type="date"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={onKeyDown}
          size="sm"
          variant="unstyled"
          bg="transparent"
          px={2}
          py={1}
          h="32px"
          borderRadius="none"
          borderWidth="1px"
          borderColor={error ? 'red.400' : 'border-default'}
          _hover={{ borderColor: error ? 'red.500' : 'border-hover' }}
          _focus={{ borderColor: 'brand.500', outline: 'none' }}
        />
      </Tooltip>
      {onFillStart && (
        <FillHandle onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); onFillStart(); }} />
      )}
    </Box>
  );
}

function SelectCell({ id, value, onChange, error, options, onFillStart }: CellProps & { options: FieldOption[] }) {
  return (
    <Box
      position="relative"
      sx={{ '& .fill-handle': { opacity: 0, transition: 'opacity 0.1s' }, '&:hover .fill-handle': { opacity: 1 } }}
    >
      <Tooltip label={error} isDisabled={!error} placement="top" hasArrow bg="red.500">
        <Select
          id={id}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          size="sm"
          variant="unstyled"
          bg="transparent"
          px={2}
          h="32px"
          borderRadius="none"
          borderWidth="1px"
          borderColor={error ? 'red.400' : 'border-default'}
          _hover={{ borderColor: error ? 'red.500' : 'border-hover' }}
          _focus={{ borderColor: 'brand.500', outline: 'none' }}
        >
          <option value="">—</option>
          {options.map((o, i) => (
            <option key={`${i}-${o.value}`} value={o.value}>{o.label}</option>
          ))}
        </Select>
      </Tooltip>
      {onFillStart && (
        <FillHandle onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); onFillStart(); }} />
      )}
    </Box>
  );
}

interface ImageCellProps extends CellProps {
  rowKey?: string;
  orgId: string;
  def: MasterDataFieldDefinition;
  rowId?: number;
  rowFields: Record<string, string>;
  dataset: string;
}

type UploadPhase = 'idle' | 'creating' | 'ready' | 'polling';

function ImageCell({ value, onChange, error, orgId, def, rowId, rowKey, rowFields, dataset }: ImageCellProps) {
  const toast = useToast();
  const [phase, setPhase] = useState<UploadPhase>('idle');
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [waUrl, setWaUrl] = useState<string | null>(null);

  const config = def.validation as ImageFieldConfig | null | undefined;
  const hasConfig = !!config?.chatbot_code && rowId !== undefined;

  const storageKey = rowId !== undefined
    ? `img_session:${orgId}:${dataset}:${rowId}:${def.field_name}`
    : null;

  // Resume polling if a session was active when the browser was closed
  useEffect(() => {
    if (!storageKey || value) return;
    const stored = localStorage.getItem(storageKey);
    if (stored) {
      setSessionId(stored);
      setPhase('polling');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { mutate: createSession } = useCreateImageUploadSession(orgId);
  const { data: pollData } = usePollImageUploadSession(orgId, sessionId, phase === 'polling');

  useEffect(() => {
    if (pollData?.image_url) {
      onChange(pollData.image_url);
      if (storageKey) localStorage.removeItem(storageKey);
      setPhase('idle'); setSessionId(null); setWaUrl(null);
    }
    if (pollData?.expired) {
      if (storageKey) localStorage.removeItem(storageKey);
      setPhase('idle'); setSessionId(null); setWaUrl(null);
    }
  }, [pollData, onChange, storageKey]);

  const handleUploadClick = () => {
    if (!config?.chatbot_code) return;
    setPhase('creating');
    const watermarkText = config.watermark_field
      ? (config.watermark_field === 'key' ? (rowKey ?? '') : (rowFields[config.watermark_field] ?? ''))
      : '';
    createSession(
      { dataset, row_id: rowId ?? null, field_name: def.field_name, watermark_text: watermarkText },
      {
        onSuccess: (session) => {
          if (!session.session_id) { setPhase('idle'); return; }
          if (storageKey) localStorage.setItem(storageKey, session.session_id);
          const attrs = (config.attribute_mappings ?? [])
            .filter((m) => rowFields[m.field_name])
            .map((m) => `${m.task_n}:${rowFields[m.field_name]}`)
            .join(' ');
          const parts = [config.chatbot_code, attrs, `upload_ref:${session.session_id}`].filter(Boolean).join(' ');
          const waNumber = (import.meta.env.VITE_APP_WHATSAPP_NUMBER as string | undefined) ?? '';
          setWaUrl(`https://wa.me/${waNumber}?text=${encodeURIComponent(`/start ${parts}`)}`);
          setSessionId(session.session_id);
          setPhase('ready');
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        onError: (err: any) => {
          toast.error(err?.message ?? 'Failed to start upload session');
          setPhase('idle');
        },
      }
    );
  };

  const handleCancel = () => {
    if (storageKey) localStorage.removeItem(storageKey);
    setPhase('idle'); setSessionId(null); setWaUrl(null);
  };

  return (
    <Tooltip label={error} isDisabled={!error} placement="top" hasArrow bg="red.500">
      <Box px={2} py={1} overflow="hidden">
        <Input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Image URL"
          size="sm"
          variant="unstyled"
          bg="transparent"
          px={1}
          py={0}
          h="24px"
          borderRadius="none"
          borderWidth="1px"
          borderColor={error ? 'red.400' : 'border-default'}
          _hover={{ borderColor: error ? 'red.500' : 'border-hover' }}
          _focus={{ borderColor: 'brand.500', outline: 'none' }}
        />
        {hasConfig && (
          <Box mt={1}>
            {phase === 'idle' && value && (
              <Button
                size="xs"
                leftIcon={<FiPrinter size={10} />}
                colorScheme="gray"
                variant="ghost"
                as="a"
                href={value}
                target="_blank"
                rel="noopener noreferrer"
                fontSize="xs"
              >
                Print
              </Button>
            )}
            {phase === 'idle' && !value && (
              <Button size="xs" leftIcon={<FiCamera size={10} />} colorScheme="teal" variant="ghost" onClick={handleUploadClick} fontSize="xs">
                Upload via WhatsApp
              </Button>
            )}
            {phase === 'creating' && (
              <HStack spacing={1}>
                <Spinner size="xs" color="brand.500" />
                <Text fontSize="xs" color="text-secondary">Starting...</Text>
              </HStack>
            )}
            {phase === 'ready' && waUrl && (
              <Box
                as="a"
                href={waUrl}
                target="_blank"
                rel="noopener noreferrer"
                display="inline-flex"
                alignItems="center"
                gap={1}
                px={2}
                py="2px"
                borderRadius="sm"
                bg="teal.500"
                color="white"
                fontSize="xs"
                fontWeight="medium"
                cursor="pointer"
                _hover={{ bg: 'teal.600' }}
                textDecoration="none"
                onClick={() => setPhase('polling')}
              >
                <FiCamera size={10} />
                Open WhatsApp
              </Box>
            )}
            {phase === 'polling' && (
              <HStack spacing={1}>
                <Spinner size="xs" color="brand.500" />
                <Text fontSize="xs" color="text-secondary">Waiting...</Text>
                <Button size="xs" variant="ghost" colorScheme="gray" h="auto" minH="0" py="2px" onClick={handleCancel}>
                  Cancel
                </Button>
              </HStack>
            )}
          </Box>
        )}
      </Box>
    </Tooltip>
  );
}

function BooleanCell({ value, onChange, onFillStart }: { value: string; onChange: (v: string) => void; onFillStart?: () => void }) {
  return (
    <Box
      position="relative"
      px={2}
      py={1}
      display="flex"
      alignItems="center"
      h="32px"
      sx={{ '& .fill-handle': { opacity: 0, transition: 'opacity 0.1s' }, '&:hover .fill-handle': { opacity: 1 } }}
    >
      <Checkbox
        isChecked={value === 'true'}
        onChange={(e) => onChange(e.target.checked ? 'true' : 'false')}
        colorScheme="brand"
      />
      {onFillStart && (
        <FillHandle onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); onFillStart(); }} />
      )}
    </Box>
  );
}

// ─── CellRenderer ─────────────────────────────────────────────────────────────

interface CellRendererProps {
  id?: string;
  def: MasterDataFieldDefinition;
  value: string;
  onChange: (v: string) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLElement>) => void;
  error?: string;
  orgId?: string;
  rowId?: number;
  rowKey?: string;
  rowFields?: Record<string, string>;
  dataset?: string;
  onFillStart?: () => void;
}

function CellRenderer({ id, def, value, onChange, onKeyDown, error, orgId, rowId, rowKey, rowFields, dataset, onFillStart }: CellRendererProps) {
  switch (def.data_type) {
    case 'select':
      return <SelectCell id={id} options={def.options ?? []} value={value} onChange={onChange} error={error} onFillStart={onFillStart} />;
    case 'date':
      return <DateCell id={id} value={value} onChange={onChange} onKeyDown={onKeyDown} error={error} onFillStart={onFillStart} />;
    case 'number':
      return <NumberCell id={id} value={value} onChange={onChange} onKeyDown={onKeyDown} error={error} onFillStart={onFillStart} />;
    case 'boolean':
      return <BooleanCell value={value} onChange={onChange} onFillStart={onFillStart} />;
    case 'image':
      return (
        <ImageCell
          id={id}
          value={value}
          onChange={onChange}
          error={error}
          orgId={orgId ?? ''}
          def={def}
          rowId={rowId}
          rowKey={rowKey}
          rowFields={rowFields ?? {}}
          dataset={dataset ?? ''}
        />
      );
    default:
      return <TextCell id={id} value={value} onChange={onChange} onKeyDown={onKeyDown} error={error} onFillStart={onFillStart} />;
  }
}

// ─── Validation ───────────────────────────────────────────────────────────────

function validateRows(rows: BulkRow[], fieldDefs: MasterDataFieldDefinition[], showLocationCols: boolean): Map<string, Record<string, string>> {
  const errorMap = new Map<string, Record<string, string>>();
  for (const row of rows) {
    const errors: Record<string, string> = {};
    if (!row.label.trim()) errors['label'] = 'Name is required';
    if (showLocationCols) {
      const lat = row.lat.trim();
      if (lat) {
        const n = Number(lat);
        if (isNaN(n) || n < -90 || n > 90) errors['lat'] = 'Must be between -90 and 90';
      }
      const lng = row.lng.trim();
      if (lng) {
        const n = Number(lng);
        if (isNaN(n) || n < -180 || n > 180) errors['lng'] = 'Must be between -180 and 180';
      }
    }
    for (const def of fieldDefs) {
      const val = (row.fields[def.field_name] ?? '').trim();
      if (def.required && !val) {
        errors[def.field_name] = `${def.label} is required`;
        continue;
      }
      if (!val) continue;
      if (def.data_type === 'select' && def.options?.length) {
        if (!def.options.some(o => o.value === val))
          errors[def.field_name] = 'Invalid option';
      } else if (def.data_type === 'number') {
        if (isNaN(Number(val))) errors[def.field_name] = 'Must be a number';
      } else if (def.data_type === 'date') {
        if (!/^\d{4}-\d{2}-\d{2}$/.test(val)) errors[def.field_name] = 'Must be YYYY-MM-DD';
      } else if (def.data_type === 'boolean') {
        const l = val.toLowerCase();
        if (l !== 'true' && l !== 'false' && l !== '1' && l !== '0')
          errors[def.field_name] = 'Must be true or false';
      }
    }
    if (Object.keys(errors).length > 0) errorMap.set(row._localId, errors);
  }
  return errorMap;
}

// ─── InlineTableEditor ────────────────────────────────────────────────────────

export interface InlineTableEditorProps {
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
  dataset: string;
  orgId: string;
  fieldDefs: MasterDataFieldDefinition[];
  initialRows?: MasterDataRow[];
  mode: EditorMode;
  labelColumnName?: string;
  columnConfig?: MasterDataColumnConfig;
}

export function InlineTableEditor({
  isOpen,
  onClose,
  onSaved,
  dataset,
  orgId,
  fieldDefs,
  initialRows,
  mode,
  labelColumnName = 'Name',
  columnConfig = DEFAULT_COLUMN_CONFIG,
}: InlineTableEditorProps) {
  const toast = useToast();
  const { mutate: batchSave, isPending: isSaving } = useBatchMasterData(orgId);

  const [addCount, setAddCount] = useState(5);
  const [focusedCell, setFocusedCell] = useState<{ rowIdx: number; col: string } | null>(null);
  const [fillState, setFillState] = useState<FillState | null>(null);

  // Focus the target cell whenever keyboard navigation updates focusedCell
  useEffect(() => {
    if (!focusedCell) return;
    const el = document.getElementById(`cell-${focusedCell.rowIdx}-${focusedCell.col}`);
    el?.focus();
  }, [focusedCell]);

  // Hidden fields are not shown in the table editor (add-only context), and the
  // whole set drops out when the category has Additional Info turned off.
  // managed_reference fields drive the Belongs To column instead of their own
  // column, since their value lives in parent_key rather than fields.
  const sortedDefs = columnConfig.show_additional_info
    ? [...fieldDefs].filter(def => !def.hidden && def.data_type !== 'managed_reference').sort((a, b) => a.sort_order - b.sort_order)
    : [];
  const managedRefDef = fieldDefs.find(def => def.data_type === 'managed_reference');
  const showParentKeyCol = columnConfig.show_parent_key && columnConfig.edit_parent_key;
  const showLocationCols = columnConfig.show_location && columnConfig.edit_location;

  const buildInitialState = useCallback((): BulkRow[] => {
    if (mode === 'edit' && initialRows && initialRows.length > 0) {
      return initialRows.map((r) => {
        const fields: Record<string, string> = {};
        for (const def of fieldDefs) {
          const v = r.fields[def.field_name];
          fields[def.field_name] = v == null ? '' : String(v);
        }
        return {
          _localId: uuidv4(), id: r.id, key: r.key, label: r.label, parent_key: r.parent_key ?? '',
          lat: r.lat != null ? String(r.lat) : '', lng: r.lng != null ? String(r.lng) : '',
          fields,
        };
      });
    }
    return [blankRow(fieldDefs), blankRow(fieldDefs), blankRow(fieldDefs)];
  }, [mode, initialRows, fieldDefs]);

  const [rows, dispatch] = useReducer(reducer, [], buildInitialState);

  useEffect(() => {
    if (isOpen) {
      dispatch({ type: 'RESET', rows: buildInitialState() });
      setFocusedCell(null);
      setFillState(null);
    }
  }, [isOpen, buildInitialState]);

  // Prevent text selection while drag-filling
  useEffect(() => {
    document.body.style.userSelect = fillState ? 'none' : '';
    return () => { document.body.style.userSelect = ''; };
  }, [fillState]);

  // Commit fill on mouseup anywhere in the document
  useEffect(() => {
    if (!fillState) return;
    const handleMouseUp = () => {
      const { srcRowIdx, col, value, endRow } = fillState;
      if (endRow !== srcRowIdx) {
        const lo = Math.min(srcRowIdx, endRow);
        const hi = Math.max(srcRowIdx, endRow);
        for (let i = lo; i <= hi; i++) {
          if (i === srcRowIdx) continue;
          dispatch({ type: 'SET_CELL', localId: rows[i]._localId, col, value });
        }
      }
      setFillState(null);
    };
    document.addEventListener('mouseup', handleMouseUp);
    return () => document.removeEventListener('mouseup', handleMouseUp);
  }, [fillState, rows]);

  const startFill = useCallback((rowIdx: number, col: string, value: string) => {
    setFillState({ srcRowIdx: rowIdx, col, value, endRow: rowIdx });
  }, []);

  const updateFillEndRow = useCallback((rowIdx: number) => {
    setFillState((prev) => (prev ? { ...prev, endRow: rowIdx } : null));
  }, []);

  const isFillTarget = useCallback((rowIdx: number, col: string): boolean => {
    if (!fillState || fillState.col !== col || rowIdx === fillState.srcRowIdx) return false;
    const lo = Math.min(fillState.srcRowIdx, fillState.endRow);
    const hi = Math.max(fillState.srcRowIdx, fillState.endRow);
    return rowIdx >= lo && rowIdx <= hi;
  }, [fillState]);

  const fillTargetShadow = 'inset 0 0 0 2px var(--chakra-colors-brand-500)';

  const allCols = [
    'label',
    ...(showParentKeyCol ? ['parent_key'] : []),
    ...(showLocationCols ? ['lat', 'lng'] : []),
    ...sortedDefs.map((d) => d.field_name),
  ];

  // Ref keeps handleKeyDown stable while always calling the latest handleSave
  const handleSaveRef = useRef<() => void>(() => {});

  const handleSave = useCallback(() => {
    dispatch({ type: 'CLEAR_ERRORS' });
    const errorMap = validateRows(rows, sortedDefs, showLocationCols);

    if (errorMap.size > 0) {
      for (const [localId, errors] of errorMap) {
        dispatch({ type: 'SET_ROW_ERRORS', localId, errors });
      }
      toast.error(`${errorMap.size} row${errorMap.size > 1 ? 's' : ''} have validation errors`);
      return;
    }

    const batchRows = rows
      .filter((r) => r.label.trim())
      .map((r) => ({
        id: r.id,
        label: r.label,
        // Omit parent_key/lat/lng entirely when the column is not shown, so the
        // server keeps whatever is already stored instead of clearing it.
        ...(showParentKeyCol ? { parent_key: r.parent_key.trim() || null } : {}),
        ...(showLocationCols ? { lat: r.lat.trim() ? Number(r.lat) : null, lng: r.lng.trim() ? Number(r.lng) : null } : {}),
        fields: Object.fromEntries(
          Object.entries(r.fields).filter(([, v]) => v !== '')
        ),
      }));

    if (batchRows.length === 0) {
      toast.error('No rows to save');
      return;
    }

    batchSave(
      { dataset, mode: mode === 'edit' ? 'update' : 'create', rows: batchRows },
      {
        onSuccess: (result) => {
          const count = result.created + result.updated;
          toast.success(`${count} row${count !== 1 ? 's' : ''} saved`);
          if ((result.errors ?? []).length === 0) {
            onSaved();
            onClose();
          } else {
            toast.error(`${result.errors.length} row${result.errors.length > 1 ? 's' : ''} failed`);
          }
        },
        onError: (err: any) => toast.error(err.message ?? 'Save failed'),
      }
    );
  }, [rows, sortedDefs, showParentKeyCol, showLocationCols, batchSave, dataset, mode, onSaved, onClose, toast]);

  useEffect(() => { handleSaveRef.current = handleSave; }, [handleSave]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLElement>, rowIdx: number, col: string) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
        e.preventDefault();
        handleSaveRef.current();
      } else if (e.key === 'Tab') {
        e.preventDefault();
        const colIdx = allCols.indexOf(col);
        if (e.shiftKey) {
          if (colIdx > 0) setFocusedCell({ rowIdx, col: allCols[colIdx - 1] });
          else if (rowIdx > 0) setFocusedCell({ rowIdx: rowIdx - 1, col: allCols[allCols.length - 1] });
        } else {
          if (colIdx < allCols.length - 1) setFocusedCell({ rowIdx, col: allCols[colIdx + 1] });
          else if (rowIdx < rows.length - 1) setFocusedCell({ rowIdx: rowIdx + 1, col: allCols[0] });
        }
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (rowIdx < rows.length - 1) setFocusedCell({ rowIdx: rowIdx + 1, col });
      } else if (e.key === 'Escape') {
        setFocusedCell(null);
      }
    },
    [allCols, rows.length]
  );

  const filledRows = rows.filter((r) => r.label.trim()).length;
  const totalErrors = rows.reduce((sum, r) => sum + Object.values(r._errors ?? {}).filter(Boolean).length, 0);

  return (
    <Drawer isOpen={isOpen} onClose={onClose} placement="bottom" size="full">
      <DrawerOverlay />
      <DrawerContent maxH="80vh" bg="bg-surface">
        <DrawerCloseButton />
        <DrawerHeader borderBottomWidth="1px" borderColor="border-default" py={3}>
          <HStack justify="space-between" pr={8}>
            <HStack spacing={3}>
              <Text fontWeight="semibold" fontSize="md">
                {mode === 'create' ? 'Add Rows' : 'Edit Rows'} — <Badge colorScheme="purple" textTransform="none">{dataset}</Badge>
              </Text>
              {filledRows > 0 && (
                <Text fontSize="sm" color="text-secondary">{filledRows} row{filledRows !== 1 ? 's' : ''}</Text>
              )}
            </HStack>
            <HStack spacing={2}>
              <HStack spacing={1}>
                <Input
                  type="number"
                  value={addCount}
                  onChange={(e) => setAddCount(Math.max(1, Math.min(100, Number(e.target.value))))}
                  size="sm"
                  w="60px"
                  bg="bg-surface"
                  borderColor="border-default"
                  _hover={{ borderColor: 'border-hover' }}
                  textAlign="center"
                />
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => dispatch({ type: 'ADD_ROWS', count: addCount, fieldDefs })}
                >
                  Add {addCount} row{addCount !== 1 ? 's' : ''}
                </Button>
              </HStack>
              <Button
                size="sm"
                leftIcon={<FiPlus />}
                variant="outline"
                onClick={() => dispatch({ type: 'ADD_ROWS', count: 1, fieldDefs })}
              >
                Add row
              </Button>
            </HStack>
          </HStack>
        </DrawerHeader>

        <DrawerBody p={0} overflow="auto">
          <Box overflowX="auto">
            <Table variant="simple" size="sm" style={{ borderCollapse: 'collapse', tableLayout: 'fixed', minWidth: '800px' }}>
              <Thead bg="bg-muted" style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                <Tr>
                  <Th w="40px" px={2} color="text-secondary" borderColor="border-default">#</Th>
                  <Th minW="180px" px={0} color="text-primary" borderColor="border-default">
                    {labelColumnName} <Text as="span" color="red.400">*</Text>
                  </Th>
                  {showParentKeyCol && (
                    <Th minW="130px" px={0} color="text-primary" borderColor="border-default">
                      {managedRefDef ? managedRefDef.label : 'Belongs To'}
                    </Th>
                  )}
                  {showLocationCols && (
                    <>
                      <Th minW="110px" px={0} color="text-primary" borderColor="border-default">
                        Latitude
                      </Th>
                      <Th minW="110px" px={0} color="text-primary" borderColor="border-default">
                        Longitude
                      </Th>
                    </>
                  )}
                  {sortedDefs.map((def) => (
                    <Th
                      key={def.field_name}
                      minW={def.data_type === 'image' ? '220px' : '130px'}
                      px={0}
                      color="text-primary"
                      borderColor="border-default"
                    >
                      {def.label}{def.required && <Text as="span" color="red.400"> *</Text>}
                    </Th>
                  ))}
                  <Th w="72px" px={1} borderColor="border-default" />
                </Tr>
              </Thead>
              <Tbody>
                {rows.map((row, rowIdx) => {
                  const hasRowError = Object.values(row._errors ?? {}).some(Boolean);
                  return (
                    <Tr
                      key={row._localId}
                      borderLeftWidth={hasRowError ? '3px' : '0'}
                      borderLeftColor={hasRowError ? 'red.400' : 'transparent'}
                      _hover={{ bg: 'interactive-hover' }}
                      onMouseEnter={() => { if (fillState) updateFillEndRow(rowIdx); }}
                    >
                      <Td px={2} borderColor="border-default" color="text-secondary" fontSize="xs" w="40px">
                        {rowIdx + 1}
                      </Td>
                      <Td p={0} borderColor="border-default" minW="180px"
                        style={isFillTarget(rowIdx, 'label') ? { boxShadow: fillTargetShadow } : undefined}
                      >
                        <TextCell
                          id={`cell-${rowIdx}-label`}
                          value={row.label}
                          onChange={(v) => dispatch({ type: 'SET_CELL', localId: row._localId, col: 'label', value: v })}
                          onKeyDown={(e) => handleKeyDown(e, rowIdx, 'label')}
                          error={row._errors?.['label']}
                          onFillStart={() => startFill(rowIdx, 'label', row.label)}
                        />
                      </Td>
                      {showParentKeyCol && (
                        <Td p={0} borderColor="border-default" minW="130px"
                          style={!managedRefDef && isFillTarget(rowIdx, 'parent_key') ? { boxShadow: fillTargetShadow } : undefined}
                        >
                          {managedRefDef ? (
                            <ReferenceSearchInput
                              id={`cell-${rowIdx}-parent_key`}
                              orgId={orgId}
                              dataset={(managedRefDef.validation?.reference_dataset as string) ?? ''}
                              valueField={managedRefDef.validation?.value_field === 'label' ? 'label' : 'key'}
                              value={row.parent_key}
                              onChange={(v) => dispatch({ type: 'SET_CELL', localId: row._localId, col: 'parent_key', value: v })}
                              onKeyDown={(e) => handleKeyDown(e, rowIdx, 'parent_key')}
                              error={row._errors?.['parent_key']}
                            />
                          ) : (
                            <TextCell
                              id={`cell-${rowIdx}-parent_key`}
                              value={row.parent_key}
                              onChange={(v) => dispatch({ type: 'SET_CELL', localId: row._localId, col: 'parent_key', value: v })}
                              onKeyDown={(e) => handleKeyDown(e, rowIdx, 'parent_key')}
                              error={row._errors?.['parent_key']}
                              onFillStart={() => startFill(rowIdx, 'parent_key', row.parent_key)}
                            />
                          )}
                        </Td>
                      )}
                      {showLocationCols && (
                        <>
                          <Td p={0} borderColor="border-default" minW="110px"
                            style={isFillTarget(rowIdx, 'lat') ? { boxShadow: fillTargetShadow } : undefined}
                          >
                            <NumberCell
                              id={`cell-${rowIdx}-lat`}
                              value={row.lat}
                              onChange={(v) => dispatch({ type: 'SET_CELL', localId: row._localId, col: 'lat', value: v })}
                              onKeyDown={(e) => handleKeyDown(e, rowIdx, 'lat')}
                              error={row._errors?.['lat']}
                              onFillStart={() => startFill(rowIdx, 'lat', row.lat)}
                            />
                          </Td>
                          <Td p={0} borderColor="border-default" minW="110px"
                            style={isFillTarget(rowIdx, 'lng') ? { boxShadow: fillTargetShadow } : undefined}
                          >
                            <NumberCell
                              id={`cell-${rowIdx}-lng`}
                              value={row.lng}
                              onChange={(v) => dispatch({ type: 'SET_CELL', localId: row._localId, col: 'lng', value: v })}
                              onKeyDown={(e) => handleKeyDown(e, rowIdx, 'lng')}
                              error={row._errors?.['lng']}
                              onFillStart={() => startFill(rowIdx, 'lng', row.lng)}
                            />
                          </Td>
                        </>
                      )}
                      {sortedDefs.map((def) => {
                        const colValue = row.fields[def.field_name] ?? '';
                        const isImage = def.data_type === 'image';
                        return (
                          <Td
                            key={def.field_name}
                            p={0}
                            borderColor="border-default"
                            minW={isImage ? '220px' : '130px'}
                            style={!isImage && isFillTarget(rowIdx, def.field_name) ? { boxShadow: fillTargetShadow } : undefined}
                          >
                            <CellRenderer
                              id={`cell-${rowIdx}-${def.field_name}`}
                              def={def}
                              value={colValue}
                              onChange={(v) => dispatch({ type: 'SET_CELL', localId: row._localId, col: def.field_name, value: v })}
                              onKeyDown={(e) => handleKeyDown(e, rowIdx, def.field_name)}
                              error={row._errors?.[def.field_name]}
                              orgId={orgId}
                              rowId={row.id}
                              rowKey={row.key}
                              rowFields={row.fields}
                              dataset={dataset}
                              onFillStart={!isImage ? () => startFill(rowIdx, def.field_name, colValue) : undefined}
                            />
                          </Td>
                        );
                      })}
                      <Td px={1} borderColor="border-default" w="72px">
                        <HStack spacing={0}>
                          <IconButton
                            aria-label="Duplicate row"
                            icon={<FiCopy />}
                            size="xs"
                            variant="ghost"
                            colorScheme="brand"
                            onClick={() => dispatch({ type: 'DUPLICATE_ROW', localId: row._localId })}
                          />
                          <IconButton
                            aria-label="Delete row"
                            icon={<FiTrash2 />}
                            size="xs"
                            variant="ghost"
                            colorScheme="red"
                            onClick={() => dispatch({ type: 'DELETE_ROW', localId: row._localId })}
                          />
                        </HStack>
                      </Td>
                    </Tr>
                  );
                })}
                {rows.length === 0 && (
                  <Tr>
                    <Td colSpan={2 + sortedDefs.length + 1} textAlign="center" py={8} color="text-secondary">
                      No rows yet — click "Add rows" above.
                    </Td>
                  </Tr>
                )}
              </Tbody>
            </Table>
          </Box>
        </DrawerBody>

        <DrawerFooter borderTopWidth="1px" borderColor="border-default" py={3}>
          <HStack w="full" justify="space-between">
            <VStack align="flex-start" spacing={0}>
              {totalErrors > 0 && (
                <Text fontSize="sm" color="red.500">
                  {totalErrors} validation error{totalErrors !== 1 ? 's' : ''} — fix highlighted rows before saving.
                </Text>
              )}
              <Text fontSize="xs" color="text-secondary">Tip: Tab to move between cells, Enter to move down, Ctrl+Enter to save</Text>
            </VStack>
            <HStack spacing={3}>
              <Button variant="ghost" onClick={onClose}>Cancel</Button>
              <Button
                colorScheme="brand"
                isLoading={isSaving}
                onClick={handleSave}
                isDisabled={rows.filter((r) => r.label.trim()).length === 0}
              >
                Save {filledRows > 0 ? `${filledRows} row${filledRows !== 1 ? 's' : ''}` : ''}
              </Button>
            </HStack>
          </HStack>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}
